import g from '#core/global';
import load from '#core/load';

// Selectors
const map_$ = '.scorecard-map';
const map_container_$ = '.scorecard-map__map';
const svg_$ = '.scorecard-map__svg';
const region_$ = '.region-map__region';
const markers_$ = '.scorecard-map__markers';
const marker_$ = '.scorecard-map__marker';
const popover_$ = '.scorecard-map__popover';
const popover_tag_$ = '.scorecard-map__popover-tag';
const popover_title_$ = '.scorecard-map__popover-title';
const popover_subtitle_$ = '.scorecard-map__popover-subtitle';
const popover_rank_$ = '.scorecard-map__popover-rank';
const popover_change_$ = '.scorecard-map__popover-change';
const popover_footer_$ = '.scorecard-map__popover-footer';
const popover_link_$ = '.scorecard-map__popover-link';
const popover_link_text_$ = '.scorecard-map__popover-link-text';
const select_$ = '.scorecard-map__select select';
const small_region_$ = '.scorecard-map__small-region';
const data_$ = '.scorecard-map__data';

// Class names
const region_enabled_c = 'region-map__region--enabled';
const region_disabled_c = 'region-map__region--disabled';
const region_highlight_c = 'region-map__region--highlight';
const region_active_c = 'region-map__region--active';
const region_has_cities_c = 'region-map__region--has-cities';
const popover_visible_c = 'scorecard-map__popover--visible';
const marker_active_c = 'scorecard-map__marker--active';
const small_region_disabled_c = 'scorecard-map__small-region--disabled';

// Attributes
const region_id_attr = 'data-region-id';
const city_id_attr = 'data-city-id';

// Unicode glyphs
const PLUS = '+';
const MINUS = '\u2212';

// Popover offset from anchor
const CURSOR_OFFSET = 12;

// Minimum distance from viewport edge
const VIEWPORT_BUFFER = 20;

// Brief delay lets the pointer move from a US pin into its popover CTA
const HOVER_HIDE_DELAY = 200;

// Module state
let $maps = null;
let has_map = false;

const module = {

	/**
	 * Init module
	 */
	init() {
		if (load.ran('scorecard-map')) return false;

		load.content(() => {
			has_map = assign_elements();
			if (!has_map) return false;
		});
	},

};

export default module;
module.init();


/**
 * Assign elements and initialize each map instance
 * @return {boolean}
 */
function assign_elements() {
	$maps = document.querySelectorAll(map_$);
	if (!$maps.length) return false;

	$maps.forEach($map => {
		init_map($map);
	});

	return true;
}


/**
 * Initialize a single map instance
 * @param {HTMLElement} $map
 * @return {boolean}
 */
function init_map($map) {
	const $map_container = $map.querySelector(map_container_$);
	const $svg = $map.querySelector(svg_$);
	const $markers_container = $map.querySelector(markers_$);
	const $popover = $map.querySelector(popover_$);
	const $select = $map.querySelector(select_$);
	const $data_el = $map.querySelector(data_$);
	const uses_pin_hover = $map.dataset.geographyType === 'us_pins';

	if (!$data_el) return false;

	// Instance state
	let regions_data = {};
	let current_region_id = null;
	let current_city_id = null;
	let hover_hide_timeout = null;

	// Parse region data
	try {
		regions_data = JSON.parse($data_el.textContent);
	} catch (e) {
		console.error('Scorecard Map: Failed to parse data');
		return false;
	}

	// Apply data attributes to SVG regions
	if ($svg) {
		apply_region_data($svg, regions_data);
	}

	// Interactive modes require popover
	if (!$svg || !$popover) {
		if ($select) {
			$select.addEventListener(g.change_e, () => {
				const url = $select.value;
				if (url) {
					window.location.href = url;
				}
			});
		}

		return true;
	}


	// --- Popover Content Updates ---

	/**
	 * Toggle the popover fact sheet CTA based on whether a valid URL exists
	 * @param {string} url
	 * @param {string} link_text
	 * @return {boolean}
	 */
	function update_popover_link(url, link_text) {
		const $footer = $popover.querySelector(popover_footer_$);
		const $link = $popover.querySelector(popover_link_$);
		const $link_text = $popover.querySelector(popover_link_text_$);
		const has_link = !!url;

		if (!$footer || !$link || !$link_text) return false;

		$footer.hidden = !has_link;

		if (!has_link) {
			$link.removeAttribute('href');
			$link_text.textContent = '';

			return true;
		}

		$link.href = url;
		$link_text.textContent = link_text || 'See Fact Sheet';

		return true;
	}

	/**
	 * Update popover content for a city marker
	 * @param {Object} city_data
	 * @return {boolean}
	 */
	function update_popover_for_city(city_data) {
		$popover.querySelector(popover_tag_$).textContent = city_data.tag || '';
		$popover.querySelector(popover_title_$).textContent = city_data.name || '';
		$popover.querySelector(popover_subtitle_$).textContent = city_data.state_name || '';
		$popover.querySelector(popover_rank_$).textContent = city_data.rank ? '#' + city_data.rank : '';
		$popover.querySelector(popover_change_$).textContent = format_rank_change(city_data.rank_change);
		update_popover_link(city_data.url, city_data.link_text);

		return true;
	}


	/**
	 * Update popover content for a region
	 * @param {string} region_id
	 * @return {boolean}
	 */
	function update_popover_for_region(region_id) {
		const data = regions_data[region_id];
		if (!data || !data.popover) return false;

		const popover = data.popover;
		$popover.querySelector(popover_tag_$).textContent = popover.tag || '';
		$popover.querySelector(popover_title_$).textContent = popover.city_name || data.name || '';
		$popover.querySelector(popover_subtitle_$).textContent = popover.state_name || data.name || '';
		$popover.querySelector(popover_rank_$).textContent = popover.rank ? '#' + popover.rank : '';
		$popover.querySelector(popover_change_$).textContent = format_rank_change(popover.rank_change);
		update_popover_link(popover.link_url || data.url, popover.link_text);

		return true;
	}


	// --- Popover Show/Hide Logic ---

	/**
	 * Show popover for a city marker
	 * @param {string} city_id
	 * @param {string} region_id
	 * @param {number} anchor_x
	 * @param {number} anchor_y
	 */
	function show_popover_for_city(city_id, region_id, anchor_x, anchor_y) {
		clear_active_states();

		const data = regions_data[region_id];
		if (!data || !data.cities || !data.cities[city_id]) return;

		const city_data = data.cities[city_id];
		current_region_id = region_id;
		current_city_id = city_id;

		// Highlight the region
		highlight_region($svg, region_id, true);

		// Update content
		update_popover_for_city(city_data);

		// Position and show
		position_popover_at_cursor($popover, anchor_x, anchor_y, $map_container);
		$popover.classList.add(popover_visible_c);
		$popover.setAttribute('aria-hidden', 'false');

		// Mark marker active
		const $marker = $markers_container.querySelector(`[${city_id_attr}="${city_id}"]`);
		if ($marker) $marker.classList.add(marker_active_c);
	}


	/**
	 * Show popover for a region without cities
	 * @param {string} region_id
	 * @param {number} cursor_x
	 * @param {number} cursor_y
	 */
	function show_popover_for_region(region_id, cursor_x, cursor_y) {
		clear_active_states();

		const data = regions_data[region_id];
		if (!data || !data.enabled || !data.popover) return;

		current_region_id = region_id;
		current_city_id = null;

		highlight_region($svg, region_id, true);
		activate_region($svg, region_id, true);

		// Update content
		update_popover_for_region(region_id);

		// Position and show
		position_popover_at_cursor($popover, cursor_x, cursor_y, $map_container);
		$popover.classList.add(popover_visible_c);
		$popover.setAttribute('aria-hidden', 'false');
	}


	/**
	 * Hide popover and clear active states
	 */
	function hide_popover() {
		cancel_hover_hide();
		$popover.classList.remove(popover_visible_c);
		$popover.setAttribute('aria-hidden', 'true');
		clear_active_states();
	}


	/**
	 * Cancel a queued hover dismissal.
	 */
	function cancel_hover_hide() {
		if (!hover_hide_timeout) return;

		window.clearTimeout(hover_hide_timeout);
		hover_hide_timeout = null;
	}


	/**
	 * Queue a hover dismissal so the popover remains reachable by pointer.
	 */
	function queue_hover_hide() {
		cancel_hover_hide();
		hover_hide_timeout = window.setTimeout(hide_popover, HOVER_HIDE_DELAY);
	}


	/**
	 * Clear all active/highlight states
	 */
	function clear_active_states() {
		if (current_region_id) {
			highlight_region($svg, current_region_id, false);
			activate_region($svg, current_region_id, false);
		}
		if (current_city_id) {
			const $marker = $markers_container.querySelector(`[${city_id_attr}="${current_city_id}"]`);
			if ($marker) $marker.classList.remove(marker_active_c);
		}
		current_region_id = null;
		current_city_id = null;
	}


	// --- Marker Click Handler ---

	if ($markers_container) {
		if (uses_pin_hover) {
			const $markers = $markers_container.querySelectorAll(marker_$);

			$markers.forEach($marker => {
				$marker.addEventListener(g.mouse_enter_e, () => {
					cancel_hover_hide();

					const city_id = $marker.getAttribute(city_id_attr);
					const region_id = $marker.getAttribute(region_id_attr);
					const rect = $marker.getBoundingClientRect();
					show_popover_for_city(city_id, region_id, rect.left + rect.width / 2, rect.top);
				});

				$marker.addEventListener(g.mouse_leave_e, queue_hover_hide);
			});

			$popover.addEventListener(g.mouse_enter_e, cancel_hover_hide);
			$popover.addEventListener(g.mouse_leave_e, queue_hover_hide);
		}

		$markers_container.addEventListener(g.click_e, e => {
			const $marker = e.target.closest(marker_$);
			if (!$marker) return;

			e.stopPropagation();

			const city_id = $marker.getAttribute(city_id_attr);
			const region_id = $marker.getAttribute(region_id_attr);

			if (uses_pin_hover) {
				// Pointer hover already opened the card. Keep click/tap as a
				// non-destructive fallback for touch and hybrid devices.
				const rect = $marker.getBoundingClientRect();
				show_popover_for_city(city_id, region_id, rect.left + rect.width / 2, rect.top);
			} else if (city_id === current_city_id) {
				// Clicking same marker - toggle off
				hide_popover();
			} else {
				// Position popover near marker
				const rect = $marker.getBoundingClientRect();
				show_popover_for_city(city_id, region_id, rect.left + rect.width / 2, rect.top);
			}
		});

		// Keyboard on markers
		$markers_container.addEventListener(g.key_down_e, e => {
			if (e.key !== 'Enter' && e.key !== ' ') return;

			const $marker = e.target.closest(marker_$);
			if (!$marker) return;

			e.preventDefault();
			e.stopPropagation();

			const city_id = $marker.getAttribute(city_id_attr);
			const region_id = $marker.getAttribute(region_id_attr);

			if (city_id === current_city_id) {
				hide_popover();
			} else {
				const rect = $marker.getBoundingClientRect();
				show_popover_for_city(city_id, region_id, rect.left + rect.width / 2, rect.top);
			}
		});
	}


	// --- SVG Region Click Handler (regions without cities) ---

	$svg.addEventListener(g.click_e, e => {
		const $region = e.target.closest(region_$);
		if (!$region || !$region.classList.contains(region_enabled_c)) {
			// Clicking on map but not on enabled region - close popover
			hide_popover();
			return;
		}

		// Keep this click from reaching the document handler, which would
		// immediately close the popover we are about to open.
		e.stopPropagation();

		const id = $region.getAttribute(region_id_attr);

		if (id === current_region_id) {
			// Clicking same region - close popover
			hide_popover();
		} else {
			// Show popover at cursor position
			show_popover_for_region(id, e.clientX, e.clientY);
		}
	});

	// --- Small Regions ---

	const $small_regions = $map.querySelectorAll(small_region_$);
	$small_regions.forEach($small => {
		if ($small.classList.contains(small_region_disabled_c)) return;

		$small.addEventListener(g.mouse_enter_e, () => {
			const id = $small.getAttribute(region_id_attr);
			highlight_region($svg, id, true);
		});

		$small.addEventListener(g.mouse_leave_e, () => {
			const id = $small.getAttribute(region_id_attr);
			if (id !== current_region_id) {
				highlight_region($svg, id, false);
			}
		});

		$small.addEventListener(g.click_e, e => {
			// Keep this click from reaching the document handler, which would
			// immediately close the popover we are about to open.
			e.stopPropagation();

			const id = $small.getAttribute(region_id_attr);
			const data = regions_data[id];

			if (id === current_region_id) {
				hide_popover();
			} else if (data && data.has_cities) {
				// Small region with cities: just highlight, no popover from button
				highlight_region($svg, id, true);
			} else if (data && data.popover) {
				show_popover_for_region(id, e.clientX, e.clientY);
			}
		});
	});


	// --- Click outside: close popover ---

	document.addEventListener(g.click_e, e => {
		if (!$popover.classList.contains(popover_visible_c)) return;
		if (e.target.closest(popover_$)) return;

		hide_popover();
	});


	// --- Keyboard: Escape closes popover ---

	document.addEventListener(g.key_down_e, e => {
		if (e.key !== 'Escape') return;
		if (!$popover.classList.contains(popover_visible_c)) return;

		hide_popover();
	});


	// --- Keyboard: Enter/Space on region ---

	$svg.addEventListener(g.key_down_e, e => {
		if (e.key !== 'Enter' && e.key !== ' ') return;

		const $region = e.target.closest(region_$);
		if (!$region || !$region.classList.contains(region_enabled_c)) return;

		e.preventDefault();
		const id = $region.getAttribute(region_id_attr);

		if (current_region_id === id) {
			hide_popover();
		} else {
			const rect = $region.getBoundingClientRect();
			show_popover_for_region(id, rect.left + rect.width / 2, rect.bottom);
		}
	});


	// --- Mobile Select Navigation ---

	if ($select) {
		$select.addEventListener(g.change_e, () => {
			const url = $select.value;
			if (url) {
				window.location.href = url;
			}
		});
	}
}


/**
 * Apply region data from JSON to SVG elements
 * @param {SVGElement} $svg
 * @param {Object} regions_data
 */
function apply_region_data($svg, regions_data) {
	const $regions = $svg.querySelectorAll(region_$);

	$regions.forEach($region => {
		const id = $region.getAttribute(region_id_attr);
		const data = regions_data[id];

		if (!data) return;

		// Add tier class for fill color
		if (data.rank_tier) {
			$region.classList.add(`region-map__region--tier-${data.rank_tier}`);
		}

		if (data.enabled) {
			if (data.has_cities) {
				// Region has city markers - region itself is NOT interactive
				$region.classList.add(region_has_cities_c);
				$region.classList.remove(region_disabled_c);
				$region.setAttribute('aria-label', data.name);
			} else {
				// Region without cities - whole region is interactive
				$region.classList.add(region_enabled_c);
				$region.classList.remove(region_disabled_c);
				$region.setAttribute('aria-label', data.name);

				if (data.url || data.popover) {
					$region.setAttribute('tabindex', '0');
					$region.setAttribute('role', 'button');
					$region.setAttribute('aria-haspopup', 'dialog');
				}
			}
		} else {
			$region.classList.add(region_disabled_c);
			$region.classList.remove(region_enabled_c);
			$region.setAttribute('aria-hidden', 'true');
			$region.removeAttribute('tabindex');
			$region.removeAttribute('role');
		}
	});
}


/**
 * Highlight/unhighlight a region
 * @param {SVGElement} $svg
 * @param {string} id
 * @param {boolean} highlight
 */
function highlight_region($svg, id, highlight) {
	const $region = $svg.querySelector(`[${region_id_attr}="${id}"]`);
	if (!$region) return;

	if (highlight) {
		$region.classList.add(region_highlight_c);
	} else {
		$region.classList.remove(region_highlight_c);
	}
}


/**
 * Activate/deactivate a region (selected state)
 * @param {SVGElement} $svg
 * @param {string} id
 * @param {boolean} active
 */
function activate_region($svg, id, active) {
	const $region = $svg.querySelector(`[${region_id_attr}="${id}"]`);
	if (!$region) return;

	if (active) {
		$region.classList.add(region_active_c);
	} else {
		$region.classList.remove(region_active_c);
	}
}


/**
 * Format numeric rank changes while preserving authored nonnumeric text.
 * Blank values are hidden; zero is a valid displayed value.
 * e.g. 4 → "+4 spots", -3 → "−3 spots", 0 → "0 spots", "New" → "New"
 * @param {number|string|null} value
 * @return {string}
 */
function format_rank_change(value) {
	if (value === null || value === undefined) return '';

	const authored_value = String(value).trim();
	if (!authored_value) return '';

	const numeric_value = Number(authored_value.replace('−', '-'));
	if (!Number.isFinite(numeric_value)) return authored_value;

	const abs = Math.abs(numeric_value);
	const sign = numeric_value > 0 ? PLUS : numeric_value < 0 ? MINUS : '';
	const label = abs === 1 ? 'spot' : 'spots';
	return `${sign}${abs} ${label}`;
}


/**
 * Position popover near an anchor point with viewport awareness
 * @param {HTMLElement} $popover
 * @param {number} cursor_x
 * @param {number} cursor_y
 * @param {HTMLElement} $container
 */
function position_popover_at_cursor($popover, cursor_x, cursor_y, $container) {
	const container_rect = $container.getBoundingClientRect();

	// Popover is display:block + visibility:hidden at desktop, so dimensions are measurable
	const popover_width = $popover.offsetWidth;
	const popover_height = $popover.offsetHeight;

	// Calculate position relative to container
	let rel_x = cursor_x - container_rect.left + CURSOR_OFFSET;
	let rel_y = cursor_y - container_rect.top + CURSOR_OFFSET;

	// Check viewport overflow
	const viewport_width = window.innerWidth;
	const viewport_height = window.innerHeight;

	const is_overflow_right = cursor_x + CURSOR_OFFSET + popover_width + VIEWPORT_BUFFER > viewport_width;
	const is_overflow_bottom = cursor_y + CURSOR_OFFSET + popover_height + VIEWPORT_BUFFER > viewport_height;

	if (is_overflow_right) {
		rel_x = cursor_x - container_rect.left - popover_width - CURSOR_OFFSET;
	}

	if (is_overflow_bottom) {
		rel_y = cursor_y - container_rect.top - popover_height - CURSOR_OFFSET;
	}

	// Clamp within viewport
	const min_x = VIEWPORT_BUFFER - container_rect.left;
	const min_y = VIEWPORT_BUFFER - container_rect.top;
	const max_x = viewport_width - VIEWPORT_BUFFER - container_rect.left - popover_width;
	const max_y = viewport_height - VIEWPORT_BUFFER - container_rect.top - popover_height;

	rel_x = Math.max(min_x, Math.min(rel_x, max_x));
	rel_y = Math.max(min_y, Math.min(rel_y, max_y));

	$popover.style.left = `${rel_x}px`;
	$popover.style.top = `${rel_y}px`;
}
